import { InfoTooltip } from "@impero-com/common/components/infoTooltip/tooltip";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    TimezoneKey,
    getAbbreviatedTimezoneName,
} from "@impero-com/common/utils/timezone";
import { Typography } from "antd";
import moment from "moment-timezone";
import { Control, useController, useWatch } from "react-hook-form";

import {
    ControlRecurrenceRule,
    IanaTimezoneView,
    CalendarView,
} from "api/generated/model";
import { useCalendarListQuery } from "components/calendarSelectInput/queries/useCalendarListQuery";

import { AllocatedTimeResponsible } from "../formData";
import { ControlViewFormSchema } from "../formSchema";

import { AllocatedTimePickerResponsible } from "./AllocatedTimePickerResponsible";
import { AllocatedTimePickerReviewer } from "./AllocatedTimePickerReviewer";
import * as theme from "./ControlPlanning.theme.css";

const { Title } = Typography;

interface ControlPlanningProps {
    control: Control<ControlViewFormSchema>;
    initialCalendars: CalendarView[];
    disabled?: boolean;
    id: string;
    titleLabel: string;
}

export function ControlPlanning({
    control,
    initialCalendars,
    disabled,
    id,
    titleLabel,
}: ControlPlanningProps) {
    const { t } = useTranslation();

    return (
        <>
            <div id={id} className={theme.controlPlanningSectionContainer}>
                <Title level={4}>{titleLabel}</Title>
                <div className={theme.planningTooltipContainer}>
                    <InfoTooltip text={t("CreateControl_Planning_Tooltip")} />
                </div>
            </div>
            <RHFAllocatedTimePickerResponsible
                control={control}
                initialCalendars={initialCalendars}
                disabled={disabled}
            />
            <RHFAllocatedTimePickerFirstReviewer
                control={control}
                initialCalendars={initialCalendars}
                disabled={disabled}
            />
            <RHFAllocatedTimePickerSecondReviewer
                control={control}
                initialCalendars={initialCalendars}
                disabled={disabled}
            />
        </>
    );
}

function useScheduledCalendarName(
    control: Control<ControlViewFormSchema>,
    initialCalendars: CalendarView[]
): string | undefined {
    const { data: calendars } = useCalendarListQuery(initialCalendars);
    const schedulingRule = useWatch({ control, name: "scheduling.rule" });
    const calendarId = isRuleWithCalendar(schedulingRule)
        ? (schedulingRule.calendarId ?? undefined)
        : undefined;
    return calendars?.find((calendar) => calendar.id === calendarId)?.name;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Controller for responsibleDays + sendTime */
function RHFAllocatedTimePickerResponsible({
    control,
    initialCalendars,
    disabled,
}: {
    control: Control<ControlViewFormSchema>;
    initialCalendars: CalendarView[];
    disabled?: boolean;
}) {
    const calendarName = useScheduledCalendarName(control, initialCalendars);
    const timezoneKey = useWatch({
        control,
        name: "scheduling.timezoneName",
    }) as TimezoneKey;
    const sendImmediately = useWatch({ control, name: "sendImmediately" });

    const { field: daysField } = useController({
        control,
        name: "allocatedTimes.responsibleDays",
        disabled,
    });
    const { field: sendTimeField } = useController({
        control,
        name: "allocatedTimes.sendTime",
        disabled,
    });

    const responsibleValue: AllocatedTimeResponsible = {
        value: daysField.value,
        unit: "days",
        sendTime: moment(sendTimeField.value),
    };

    const handleChange = (updated: AllocatedTimeResponsible) => {
        daysField.onChange(updated.value);
        sendTimeField.onChange(updated.sendTime.toDate());
    };

    return (
        <AllocatedTimePickerResponsible
            timezoneKey={timezoneKey}
            value={responsibleValue}
            onChange={handleChange}
            calendarName={calendarName}
            disabled={disabled}
            sendImmediately={sendImmediately}
        />
    );
}

/** Controller for allocatedTimes.firstReviewer */
function RHFAllocatedTimePickerFirstReviewer({
    control,
    initialCalendars,
    disabled,
}: {
    control: Control<ControlViewFormSchema>;
    initialCalendars: CalendarView[];
    disabled?: boolean;
}) {
    const calendarName = useScheduledCalendarName(control, initialCalendars);
    const { field } = useController({
        control,
        name: "allocatedTimes.firstReviewer",
        disabled,
    });

    return (
        <AllocatedTimePickerReviewer
            target="firstReviewer"
            value={field.value}
            onChange={field.onChange}
            calendarName={calendarName}
            disabled={disabled}
        />
    );
}

/** Controller for allocatedTimes.secondReviewer */
function RHFAllocatedTimePickerSecondReviewer({
    control,
    initialCalendars,
    disabled,
}: {
    control: Control<ControlViewFormSchema>;
    initialCalendars: CalendarView[];
    disabled?: boolean;
}) {
    const calendarName = useScheduledCalendarName(control, initialCalendars);
    const { field } = useController({
        control,
        name: "allocatedTimes.secondReviewer",
        disabled,
    });

    return (
        <AllocatedTimePickerReviewer
            target="secondReviewer"
            value={field.value}
            onChange={field.onChange}
            calendarName={calendarName}
            disabled={disabled}
        />
    );
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export function timeToString(
    hour: number,
    minute: number,
    timezone: IanaTimezoneView
) {
    const tzName = timezone.name;
    const date = moment({ hour, minute });
    const formattedTime =
        date.format("H:mm") +
        " " +
        getAbbreviatedTimezoneName(tzName as TimezoneKey);
    return formattedTime;
}

function isRuleWithCalendar(
    rule: ControlRecurrenceRule
): rule is Exclude<ControlRecurrenceRule, { type: "Date" | "Weekly" }> {
    return rule.type !== "Date" && rule.type !== "Weekly";
}
